<template>
  <div
    class="kanban-page"
    :class="{ 'kanban-page--scrollable': activeView === 'dashboard' }"
  >
    <div class="kanban-page__header">
      <h1 class="kanban-page__title">{{ $t('OPPORTUNITY_KANBAN.TITLE') }}</h1>
      <div class="view-switcher" role="tablist">
        <button
          class="view-switcher__option"
          :class="{ 'view-switcher__option--active': activeView === 'kanban' }"
          role="tab"
          :aria-selected="activeView === 'kanban'"
          @click="setActiveView('kanban')"
        >
          {{ $t('OPPORTUNITY_KANBAN.VIEW_SWITCHER.KANBAN') }}
        </button>
        <button
          v-if="isDashboardAccessAllowed"
          class="view-switcher__option"
          :class="{
            'view-switcher__option--active': activeView === 'dashboard',
          }"
          role="tab"
          :aria-selected="activeView === 'dashboard'"
          @click="setActiveView('dashboard')"
        >
          {{ $t('OPPORTUNITY_KANBAN.VIEW_SWITCHER.DASHBOARD') }}
        </button>
      </div>
      <div class="kanban-page__actions">
        <woot-button
          v-if="showDashboardExport"
          color-scheme="success"
          icon="arrow-download"
          size="small"
          class="dashboard-export"
          :title="dashboardExportTitle"
          :is-loading="isExportingDashboard"
          :is-disabled="isDashboardExportDisabled"
          @click="exportDashboard"
        >
          {{ $t('OPPORTUNITY_DASHBOARD.EXPORT.BUTTON') }}
        </woot-button>
        <button
          type="button"
          class="filter-toggle"
          :class="{ 'filter-toggle--active': showFilters }"
          :aria-expanded="showFilters"
          @click="showFilters = !showFilters"
        >
          <fluent-icon icon="filter" size="16" />
          <span>{{
            $t('OPPORTUNITY_KANBAN.VIEW_SWITCHER.TOGGLE_FILTERS')
          }}</span>
          <fluent-icon
            :icon="showFilters ? 'chevron-up' : 'chevron-down'"
            size="16"
          />
        </button>
      </div>
    </div>

    <empty-state
      v-if="!isFetchingPipelines && !pipelines.length"
      :image-src="emptyPipelinesImage"
      :title="$t('OPPORTUNITY_KANBAN.NO_PIPELINES_TITLE')"
      :message="$t('OPPORTUNITY_KANBAN.NO_PIPELINES_MESSAGE')"
    >
      <div class="medium-12 columns text-center">
        <router-link
          class="button nice"
          :to="{ name: 'settings_opportunity_pipelines_list' }"
        >
          {{ $t('OPPORTUNITY_KANBAN.NO_PIPELINES_BUTTON') }}
        </router-link>
      </div>
    </empty-state>

    <template v-else-if="activeView === 'kanban'">
      <opportunity-kanban-filters
        v-if="showFilters"
        :pipelines="pipelines"
        :agents="agents"
        :inboxes="inboxes"
        :disabled="isFetchingBoard"
        :initial-filters="filters"
        :show-owner-filter="isOwnerFilterAllowed"
        @filter-applied="onFilterApplied"
      />

      <div
        v-if="isPipelineInactive"
        class="status-banner status-banner--locked"
      >
        <span class="status-banner-icon">🔒</span>
        <div class="status-banner-text">
          <p class="status-banner-title">
            {{ $t('OPPORTUNITY_DETAILS.STATUS_BANNER.LOCKED_TITLE') }}
          </p>
          <p class="status-banner-subtitle">
            {{ $t('OPPORTUNITY_DETAILS.STATUS_BANNER.LOCKED_SUBTITLE') }}
          </p>
        </div>
      </div>

      <empty-state
        v-if="!filters.pipelineId"
        :title="$t('OPPORTUNITY_KANBAN.SELECT_PIPELINE_TITLE')"
        :message="$t('OPPORTUNITY_KANBAN.SELECT_PIPELINE_MESSAGE')"
      />
      <opportunity-kanban-board
        v-else
        :columns="columns"
        :filters="filters"
        :is-fetching="isFetchingBoard"
        :pipeline-locked="isPipelineInactive"
        @refresh="loadBoard"
      />
    </template>

    <opportunity-dashboard-view
      v-else
      :pipelines="pipelines"
      :agents="agents"
      :inboxes="inboxes"
      :initial-filters="dashboardInitialFilters"
      :show-filters="showFilters"
      @pipeline-selected="onDashboardPipelineSelected"
      @filters-applied="onDashboardFiltersApplied"
      @total-changed="onDashboardTotalChanged"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { debounce } from '@chatwoot/utils';
import alertMixin from 'shared/mixins/alertMixin';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import EmptyState from 'dashboard/components/widgets/EmptyState';
import {
  buildOpportunityFilterParams,
  DEFAULT_KANBAN_FILTERS,
} from 'dashboard/helper/opportunityKanbanHelper';
import { buildOpportunityDashboardParams } from 'dashboard/helper/opportunityDashboardHelper';
import OpportunityKanbanFilters from './kanban/OpportunityKanbanFilters.vue';
import OpportunityKanbanBoard from './kanban/OpportunityKanbanBoard.vue';
import OpportunityDashboardView from './dashboard/OpportunityDashboardView.vue';
import emptyPipelinesImage from '../../../../assets/images/opportunity-pipelines/empty-list.svg';

export default {
  components: {
    EmptyState,
    OpportunityKanbanFilters,
    OpportunityKanbanBoard,
    OpportunityDashboardView,
  },
  mixins: [alertMixin],
  data() {
    return {
      emptyPipelinesImage,
      pipelines: [],
      pipelineDetail: null,
      columns: [],
      filters: DEFAULT_KANBAN_FILTERS(),
      dashboardFilters: {},
      dashboardTotal: null,
      isExportingDashboard: false,
      isFetchingBoard: false,
      isFetchingPipelines: true,
      activeView: 'kanban',
      showFilters: false,
    };
  },
  computed: {
    ...mapGetters({
      agents: 'agents/getAgents',
      inboxes: 'inboxes/getInboxes',
      currentAccountId: 'getCurrentAccountId',
      isOpportunityDashboardAccessAllowed:
        'accounts/isOpportunityDashboardAccessAllowed',
      isOwnerFilterAllowed: 'accounts/isOpportunityOwnerFilterAllowed',
    }),
    isDashboardAccessAllowed() {
      return this.isOpportunityDashboardAccessAllowed(this.currentAccountId);
    },
    showDashboardExport() {
      return (
        this.activeView === 'dashboard' &&
        this.isDashboardAccessAllowed &&
        this.pipelines.length > 0
      );
    },
    isDashboardExportDisabled() {
      return (
        !this.dashboardFilters.pipelineId ||
        !this.dashboardTotal ||
        this.isExportingDashboard
      );
    },
    dashboardExportTitle() {
      return this.dashboardTotal === 0
        ? this.$t('OPPORTUNITY_DASHBOARD.EXPORT.EMPTY')
        : '';
    },
    isPipelineInactive() {
      return !!this.pipelineDetail && this.pipelineDetail.status === 'inactive';
    },
    dashboardInitialFilters() {
      return {
        ...this.dashboardFilters,
        pipelineId: this.dashboardFilters.pipelineId || this.filters.pipelineId,
      };
    },
  },
  created() {
    this.debouncedRefreshBoard = debounce(this.loadBoard, 800);
  },
  mounted() {
    this.fetchFilterOptions();
    window.bus.$on('opportunity_updated', this.handleOpportunityUpdatedEvent);
  },
  beforeDestroy() {
    window.bus.$off('opportunity_updated', this.handleOpportunityUpdatedEvent);
  },
  methods: {
    handleOpportunityUpdatedEvent(data) {
      if (this.activeView !== 'kanban' || !this.filters.pipelineId) return;
      if (Number(data?.pipeline_id) !== Number(this.filters.pipelineId)) return;

      this.debouncedRefreshBoard();
    },
    setActiveView(view) {
      if (this.activeView === view) return;
      if (view === 'dashboard' && !this.isDashboardAccessAllowed) return;

      this.activeView = view;
      if (view === 'kanban') {
        this.dashboardTotal = null;
        this.loadBoard();
      }
    },
    onDashboardPipelineSelected(pipelineId) {
      if (pipelineId) {
        this.filters = { ...this.filters, pipelineId };
      }
    },
    onDashboardFiltersApplied(filters) {
      this.dashboardFilters = filters;
    },
    onDashboardTotalChanged(total) {
      this.dashboardTotal = total;
    },
    async exportDashboard() {
      if (this.isDashboardExportDisabled) return;

      this.isExportingDashboard = true;
      try {
        await this.$store.dispatch(
          'opportunities/export',
          buildOpportunityDashboardParams(this.dashboardFilters)
        );
        this.showAlert(this.$t('OPPORTUNITY_DASHBOARD.EXPORT.SUCCESS'));
      } catch (error) {
        this.showAlert(
          parseAPIErrorResponse(error) ||
            this.$t('OPPORTUNITY_DASHBOARD.EXPORT.ERROR')
        );
      } finally {
        this.isExportingDashboard = false;
      }
    },
    async fetchFilterOptions() {
      this.isFetchingPipelines = true;
      try {
        await Promise.all([
          this.$store.dispatch('opportunityPipelines/get'),
          this.$store.dispatch('agents/get'),
          this.$store.dispatch('inboxes/get'),
        ]);
        this.pipelines = this.$store.getters[
          'opportunityPipelines/getPipelines'
        ];

        const [firstActivePipeline] = this.pipelines.filter(
          pipeline => pipeline.status === 'active'
        );
        if (!this.filters.pipelineId && firstActivePipeline) {
          this.filters = {
            ...this.filters,
            pipelineId: firstActivePipeline.id,
          };
          this.loadBoard();
        }
      } finally {
        this.isFetchingPipelines = false;
      }
    },
    onFilterApplied(filters) {
      this.filters = filters;
      this.loadBoard();
    },
    async loadBoard() {
      if (!this.filters.pipelineId) {
        this.columns = [];
        this.pipelineDetail = null;
        return;
      }

      const { pipelineId } = this.filters;
      this.isFetchingBoard = true;
      try {
        const [pipelineDetail, board] = await Promise.all([
          this.$store.dispatch('opportunityPipelines/show', pipelineId),
          this.$store.dispatch(
            'opportunities/board',
            buildOpportunityFilterParams(this.filters)
          ),
        ]);

        if (pipelineId !== this.filters.pipelineId) return;

        this.pipelineDetail = pipelineDetail;
        this.columns = board.columns;
      } catch (error) {
        this.showAlert(
          parseAPIErrorResponse(error) ||
            this.$t('OPPORTUNITY_KANBAN.LOAD_ERROR')
        );
      } finally {
        this.isFetchingBoard = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.kanban-page {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  padding: var(--space-normal) var(--space-large);
  width: 100%;
}

.kanban-page--scrollable {
  overflow-y: auto;
}

.kanban-page__header {
  align-items: center;
  display: flex;
  gap: var(--space-normal);
  margin-bottom: var(--space-normal);
}

.kanban-page__title {
  flex-shrink: 0;
  font-size: var(--font-size-big);
  margin: 0;
}

.view-switcher {
  background: var(--s-25);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-medium);
  display: flex;
  padding: var(--space-micro);
}

.view-switcher__option {
  background: transparent;
  border: none;
  border-radius: var(--border-radius-small);
  color: var(--s-600);
  cursor: pointer;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  padding: var(--space-smaller) var(--space-normal);
  transition: background 0.15s ease, color 0.15s ease;

  &:hover:not(&--active) {
    background: var(--s-50);
    color: var(--s-800);
  }

  &--active {
    background: var(--w-500);
    box-shadow: var(--shadow-small);
    color: var(--white);
    font-weight: var(--font-weight-bold);
  }
}

.kanban-page__actions {
  align-items: center;
  display: flex;
  gap: var(--space-small);
  margin-left: auto;
}

.filter-toggle {
  align-items: center;
  background: var(--white);
  border: 1px solid var(--s-200);
  border-radius: var(--border-radius-medium);
  color: var(--s-700);
  cursor: pointer;
  display: flex;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  gap: var(--space-smaller);
  padding: var(--space-smaller) var(--space-normal);
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--s-25);
    border-color: var(--s-300);
  }

  &--active {
    background: var(--b-50);
    border-color: var(--b-200);
    color: var(--b-700);
  }
}

.status-banner {
  align-items: flex-start;
  border: 1px solid transparent;
  border-radius: var(--border-radius-medium);
  display: flex;
  gap: var(--space-normal);
  margin-bottom: var(--space-normal);
  padding: var(--space-normal) var(--space-medium);
}

.status-banner-icon {
  font-size: 1.75rem;
  line-height: 1;
}

.status-banner-title {
  font-size: var(--font-size-medium);
  font-weight: var(--font-weight-bold);
  margin: 0;
}

.status-banner-subtitle {
  font-size: var(--font-size-small);
  margin: var(--space-micro) 0 0;
}

.status-banner--locked {
  background: var(--y-50);
  border-color: var(--y-200);
  color: var(--y-800);
}
</style>
